import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for postal code validation
 */
export interface PostalCodeValidatorOptions extends BaseValidatorOptions {}

/**
 * Validate a postal code value
 * Supports US ZIP codes (5 digits or 5+4 format) and international postal codes
 * @param value - The postal code string to validate
 * @param options - Validation options
 * @returns Validation result with the postal code value and optional error (never null)
 *
 * @example
 * ```typescript
 * validatePostalCode("12345");        // { value: "12345" }
 * validatePostalCode("12345-6789");   // { value: "12345-6789" }
 * validatePostalCode("ABC123");       // { value: "ABC123" }
 * validatePostalCode("12");           // { value: "12", error: { ... } }
 * ```
 */
export function validatePostalCode(value: string, options: PostalCodeValidatorOptions = {}): ValidationResult<string> {
  const { field = "value" } = options;
  // US ZIP code (5 digits or 5+4 format) or international postal codes
  const postalRegex = /^(\d{5}(-\d{4})?|[A-Z0-9]{3,10})$/i;

  if (!postalRegex.test(value)) {
    return {
      value,
      error: {
        field,
        message: `"${field}" does not match required postal code pattern: ${value}`,
        severity: "warning",
      },
    };
  }

  return { value };
}
